package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.manyverse.app.presentation.error.toUserMessage
import fr.manyverse.app.presentation.state.AsyncUiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

/**
 * Base ViewModel class providing common utilities for async operations.
 * Eliminates the repetitive try-catch pattern found across ViewModels.
 *
 * Usage:
 * ```kotlin
 * class MyViewModel : BaseViewModel() {
 *     private val _state = MutableStateFlow<AsyncUiState<Data>>(AsyncUiState.Idle)
 *     val state = _state.asStateFlow()
 *
 *     fun loadData() {
 *         launchAsync(_state) {
 *             myUseCase()
 *         }
 *     }
 * }
 * ```
 */
abstract class BaseViewModel : ViewModel() {

    /**
     * Launches an async operation and automatically handles loading/success/error states.
     * This eliminates the repetitive pattern of:
     * ```
     * viewModelScope.launch {
     *     _state.value = Loading
     *     useCase().fold(
     *         onSuccess = { _state.value = Success(it) },
     *         onFailure = { _state.value = Error(it.toUserMessage()) }
     *     )
     * }
     * ```
     *
     * @param state The MutableStateFlow to update with loading/success/error states
     * @param block The suspend function that returns a Result<T>
     * @return The launched Job for potential cancellation
     */
    protected fun <T> launchAsync(
        state: MutableStateFlow<AsyncUiState<T>>,
        block: suspend () -> Result<T>
    ): Job = viewModelScope.launch {
        state.value = AsyncUiState.Loading
        block()
            .onSuccess { data ->
                state.value = AsyncUiState.Success(data)
            }
            .onFailure { exception ->
                state.value = AsyncUiState.Error(exception.toUserMessage())
            }
    }

    /**
     * Launches an async operation for actions that don't return data (Unit result).
     * Sets state to Success(Unit) on success.
     *
     * @param state The MutableStateFlow to update
     * @param block The suspend function that returns a Result<Unit>
     * @return The launched Job
     */
    protected fun launchAction(
        state: MutableStateFlow<AsyncUiState<Unit>>,
        block: suspend () -> Result<Unit>
    ): Job = launchAsync(state, block)

    /**
     * Launches an async operation with custom success handling.
     * Useful when you need to do additional processing after success.
     *
     * @param state The MutableStateFlow to update
     * @param block The suspend function that returns a Result<T>
     * @param onFailure Called with the failure, after state is updated. Lets an action whose
     *   Error state no screen displays still reach the user (e.g. `errorNotifier::notify`)
     * @param onSuccess Called with the result data on success, after state is updated
     * @return The launched Job
     */
    protected fun <T> launchAsyncWithCallback(
        state: MutableStateFlow<AsyncUiState<T>>,
        block: suspend () -> Result<T>,
        onFailure: (Throwable) -> Unit = {},
        onSuccess: suspend (T) -> Unit = {}
    ): Job = viewModelScope.launch {
        state.value = AsyncUiState.Loading
        block()
            .onSuccess { data ->
                state.value = AsyncUiState.Success(data)
                onSuccess(data)
            }
            .onFailure { exception ->
                state.value = AsyncUiState.Error(exception.toUserMessage())
                onFailure(exception)
            }
    }

    /**
     * Launches an async operation that transforms the result before updating state.
     *
     * @param state The MutableStateFlow to update
     * @param block The suspend function that returns a Result<T>
     * @param transform Function to transform the result before storing in state
     * @return The launched Job
     */
    protected fun <T, R> launchAsyncMapped(
        state: MutableStateFlow<AsyncUiState<R>>,
        block: suspend () -> Result<T>,
        transform: (T) -> R
    ): Job = viewModelScope.launch {
        state.value = AsyncUiState.Loading
        block()
            .onSuccess { data ->
                state.value = AsyncUiState.Success(transform(data))
            }
            .onFailure { exception ->
                state.value = AsyncUiState.Error(exception.toUserMessage())
            }
    }

    /**
     * Launches an async operation without setting Loading state first.
     * Useful for background refreshes where you don't want to show loading indicator.
     *
     * @param state The MutableStateFlow to update
     * @param block The suspend function that returns a Result<T>
     * @return The launched Job
     */
    protected fun <T> launchAsyncSilent(
        state: MutableStateFlow<AsyncUiState<T>>,
        block: suspend () -> Result<T>
    ): Job = viewModelScope.launch {
        block()
            .onSuccess { data ->
                state.value = AsyncUiState.Success(data)
            }
            .onFailure { exception ->
                // Only update to error if we're not already showing success data
                if (state.value !is AsyncUiState.Success) {
                    state.value = AsyncUiState.Error(exception.toUserMessage())
                }
            }
    }

    /**
     * Resets a state to Idle.
     *
     * @param state The MutableStateFlow to reset
     */
    protected fun <T> resetState(state: MutableStateFlow<AsyncUiState<T>>) {
        state.value = AsyncUiState.Idle
    }

    /**
     * Creates a cancellable async operation.
     * Returns a function that can be used to launch and auto-cancel previous operations.
     *
     * Usage:
     * ```kotlin
     * private val loadData = cancellableAsync<List<Data>>()
     *
     * fun load() {
     *     loadData(_state) { myUseCase() }
     * }
     * ```
     */
    protected fun <T> cancellableAsync(): CancellableAsyncLauncher<T> {
        return CancellableAsyncLauncher(viewModelScope)
    }

    /**
     * Helper class for managing cancellable async operations.
     */
    protected class CancellableAsyncLauncher<T>(
        private val scope: CoroutineScope
    ) {
        private var currentJob: Job? = null

        operator fun invoke(
            state: MutableStateFlow<AsyncUiState<T>>,
            block: suspend () -> Result<T>
        ): Job {
            currentJob?.cancel()
            return scope.launch {
                state.value = AsyncUiState.Loading
                block()
                    .onSuccess { data ->
                        state.value = AsyncUiState.Success(data)
                    }
                    .onFailure { exception ->
                        state.value = AsyncUiState.Error(exception.toUserMessage())
                    }
            }.also { currentJob = it }
        }

        fun cancel() {
            currentJob?.cancel()
            currentJob = null
        }
    }
}
